import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Client, Factory, Service, Support } from '../roles/roles';
import axios from 'axios';
import { useEffect } from 'react';

export enum AuthStatus {
    SignedIn,
    SignedOut,
    Loading
}

export enum Role {
  Client,
  Factory,
  Service,
  Support
}

export type UserModel = {
  id: number;
  role: number;
  username: string;
  profile_image: string;
  first_name: string;
  last_name: string;
  middle_name: string; 
  organization_id: any;
  role_slug: string;
}

// Define the shape of the context data
export type AuthContextType = {
    authStatus: AuthStatus;
    authMessage: string;
    user: UserModel | undefined;
    login: (username: string, password: string, remember: boolean) => void;
    logout: () => void;
    roleToRepresentation: () => string;
}


export type SignedInContextType = {
  authStatus: AuthStatus;
  authMessage: string;
  user: UserModel;
  login: (username: string, password: string, remember: boolean) => void;
  logout: () => void;
  roleToRepresentation: () => string;
}

type Props = {
    children?: React.ReactNode;
};


interface AuthProviderProps {
  children: ReactNode;
}


const defaultState: AuthContextType = {
  authStatus: AuthStatus.SignedOut,
  authMessage: " ",
  user : undefined,
  login : () => {},
  logout: () => {},
  roleToRepresentation: () => ""
}


// Create the context with a default value
const AuthContext = createContext<AuthContextType>(defaultState);


export const AuthIsSignedIn = ({ children }: Props) => {
    const { authStatus, user }: AuthContextType = useContext(AuthContext);
    return <>{authStatus === AuthStatus.SignedIn && user !== undefined ? children : null}</>;
};


export const AuthClientSignedIn = ({ children }: Props) => {
  const { authStatus, user }: AuthContextType = useContext(AuthContext);
  return <>{authStatus === AuthStatus.SignedIn && user !== undefined && user.role === Client ? children : null}</>;
}


export const AuthFactorySignedIn = ({ children }: Props) => {
  const { authStatus, user }: AuthContextType = useContext(AuthContext);
  return <>{authStatus === AuthStatus.SignedIn && user !== undefined && user.role === Factory ? children : null}</>;
}


export const AuthServiceSignedIn = ({ children }: Props) => {
  const { authStatus, user }: AuthContextType = useContext(AuthContext);
  return <>{authStatus === AuthStatus.SignedIn && user !== undefined && user.role === Service ? children : null}</>;
}


export const AuthSupportSignedIn = ({ children }: Props) => {
  const { authStatus, user }: AuthContextType = useContext(AuthContext);
  return <>{authStatus === AuthStatus.SignedIn && user !== undefined && user.role === Support ? children : null}</>;
}


export const AuthIsNotSignedIn = ({ children }: Props) => {
    const { authStatus }: AuthContextType = useContext(AuthContext);
    return <>{authStatus === AuthStatus.SignedOut ? children : null}</>;
};


export const AuthIsLoading = ({ children }: Props) => {
  const { authStatus }: AuthContextType = useContext(AuthContext);
  return <>{authStatus === AuthStatus.Loading ? children : null}</>;
};


export const isAuthenticated = async () => {
  const cookieValue = document.cookie.match(/csrftoken=([^;]+)/);
  if (cookieValue) {
      const headers = {
          'Content-Type': 'application/json'
      };
      try {
          const response = await axios.get<UserModel>(`${process.env.REACT_APP_DJANGO_SERVER_URL}/api/auth/isauthenticated`, {
              headers,
              withCredentials: true
          });
          return response;
          // return AuthStatus.SignedIn;
      } catch (error) {
          return undefined
          // return AuthStatus.SignedOut; // Return false if there's an error during authentication
      }
  } else {
    return undefined
      // return AuthStatus.SignedOut; // Return false if cookie is not present
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }: AuthProviderProps) => {
  const [authStatus, setAuthStatus] = useState<AuthStatus>(AuthStatus.Loading);
  const [authMessage, setAuthMessage] = useState<string>("");
  const [user, setUserData] = useState<UserModel | undefined>(undefined);
  const [lastChecked, setLastChecked] = useState<number>(0);

  useEffect(() => {
    const fetchAuthState = async () => {
      const response = await isAuthenticated()
      if (response?.status === 200) {
        const usr:UserModel = response.data
        usr['role_slug'] = roleToSlug(usr.role)
        setUserData(usr)
        setAuthStatus(AuthStatus.SignedIn)
      } else {
        setAuthStatus(AuthStatus.SignedOut)
      }
    }
    const now = Date.now();

    if (now - lastChecked < 60000) {
      return;
    }
    fetchAuthState()
    setLastChecked(now);
    // TODO change logic
  }, [])

  function roleToSlug(role: number):string {
    switch (role) {
      case 1:
        return 'client';
      case 6:
      case 7:
      case 8:
        return 'staff';
      default:
        return 'unknown'
    }
  } 
  function roleToRepresentation(): string {
    switch (user?.role) {
      case 1:
        return 'Клиент';
      case 6:
        return 'Завод';
      case 7:
        return 'Сервис';
      case 8:
        return 'Поддержка';
      default:
        return 'unknown'
    }
  }

  function login(username: string, password:string, remember: boolean) {
    axios.post(`${process.env.REACT_APP_DJANGO_SERVER_URL}/api/auth/login`,
      {         
        username:username,
        password:password,
        remember_me: remember
      },
      {
        withCredentials: true,
      },
    )
    .then(response => {
      if (response.status === 200) {
        setAuthStatus(AuthStatus.SignedIn)
        window.location.href = '/react/'
      }
    })
    .catch(error => {
      if (!error.response) {
        return void setAuthMessage("Возникли временные неполадки попробуйте снова");
      }
      let responseDetail:string | null = error.response.data.detail
      if (error.response.status === 403 && typeof(responseDetail) === "string" ) {
        if (responseDetail.includes("CSRF Failed")) {
            setAuthMessage("Возникли временные неполадки попробуйте снова")
            // logout(setAuthCtx)
        } else {
            setAuthMessage("Возникла непредвиденная ошибка")
            // logout(setAuthCtx)
        }
      } else {
        setAuthMessage("Неверный логин или пароль")
      }
    });
  }

  function logout() {
    let cookieValue = document.cookie.match(/csrftoken=([^;]+)/);
    if (cookieValue) {
        const headers = {
            'Content-Type': 'application/json'
        };
        // check if user is authorised on backend
        axios.get(`${process.env.REACT_APP_DJANGO_SERVER_URL}/api/auth/logout`,
                { headers, withCredentials:true }    
            )
            .then(response => {
                setAuthStatus(AuthStatus.SignedOut)
            })
            .catch(error => {
                console.log("bad response",error)
            })
      }
  }

  return (
    <AuthContext.Provider value={{ authStatus, authMessage, user, login, logout, roleToRepresentation }}>
      {children}
    </AuthContext.Provider>
  );
};


// Create a custom hook to use the context
export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useMyContext must be used within a MyProvider');
  }
  return context;
};


export const useSignedInContext = (): SignedInContextType => {
  const context = useContext(AuthContext);
  if (!context || !context.user) {
      throw new Error('Cannot convert AuthContextType to SignedInContextType because user is undefined');
  }

  const { authStatus, authMessage, user, login, logout, roleToRepresentation } = context;

  return {
      authStatus,
      authMessage,
      user, 
      login,
      logout,
      roleToRepresentation,
  };
};
